import { Polygon, Response, testPolygonPolygon, Vector } from 'sat';
import Konva from 'konva';
import { Geom, intersection } from 'polygon-clipping';
import { FACET_HIGHLIGHT_COLOR, FACET_HIGHLIGHT_OFF_COLOR } from '../../constants';
import { inchesToDecimeters, metersToInches } from 'unit.utils';

export type Point = [number, number];
export type RectPoints = number[];

export function degToRad(deg: number) {
	return (deg * Math.PI) / 180;
}
export function getSnappedPosition(
	position: { x: number; y: number },
	currentShape: any,
	siblings: Konva.Node[]
) {
	const snapThreshold = 5;
	let closestSnapPoint: any = null;
	let closestDistance = Infinity;

	// Convert current shape to an SAT polygon (with the new position)
	const currentPolygon = convertToSATPolygon(currentShape, position);

	// Iterate through sibling shapes
	siblings.forEach((sibling) => {
		if (!(sibling instanceof Konva.Line)) return;

		// Convert sibling shape to SAT polygon
		const siblingPos = sibling.position();
		const siblingPolygon = convertToSATPolygon(sibling, siblingPos);

		// Check for collision using SAT.js
		const response = new Response();
		const collided = testPolygonPolygon(currentPolygon, siblingPolygon, response);

		if (collided) {
			// If collided, resolve the overlap immediately
			const overlapVector = response.overlapV.clone().scale(-1); // Push currentShape away
			const snappedX = position.x + overlapVector.x;
			const snappedY = position.y + overlapVector.y;

			closestSnapPoint = { x: snappedX, y: snappedY };
		} else {
			// No collision, check snapping based on vertices (edges) of the shapes
			const currentVertices = currentPolygon.points;
			const siblingVertices = siblingPolygon.points;

			// Check each vertex of the current shape against all vertices of the sibling shape
			currentVertices.forEach((currentVertex) => {
				siblingVertices.forEach((siblingVertex) => {
					const distance = Math.hypot(
						siblingVertex.x - currentVertex.x,
						siblingVertex.y - currentVertex.y
					);

					// If the distance is within the snapping threshold, update closestSnapPoint
					if (distance < snapThreshold && distance < closestDistance) {
						const snapOffsetX = siblingVertex.x - currentVertex.x;
						const snapOffsetY = siblingVertex.y - currentVertex.y;

						closestSnapPoint = {
							x: position.x + snapOffsetX,
							y: position.y + snapOffsetY,
						};
						closestDistance = distance;
					}
				});
			});
		}
	});

	// Return the snapped position (or null if no snapping is needed)
	return closestSnapPoint ? { x: closestSnapPoint.x, y: closestSnapPoint.y } : null;
}

// Utility to convert a Konva shape (rotated Line) into an SAT.js polygon
function convertToSATPolygon(shape: Konva.Line, position: { x: number; y: number }): Polygon {
	const points = shape.points();
	const vectors = [];
	const rotation = shape.rotation(); // Rotation in degrees
	const rotationRad = degToRad(rotation); // Convert to radians
	const shapeCenter = shape.getClientRect(); // Get bounding box to calculate the center

	// Apply rotation to each point and convert to SAT.js vectors
	for (let i = 0; i < points.length; i += 2) {
		const x = points[i];
		const y = points[i + 1];

		// Calculate the rotated position using the rotation matrix
		const rotatedX =
			Math.cos(rotationRad) * (x - shapeCenter.width / 2) -
			Math.sin(rotationRad) * (y - shapeCenter.height / 2) +
			shapeCenter.width / 2 +
			position.x;
		const rotatedY =
			Math.sin(rotationRad) * (x - shapeCenter.width / 2) +
			Math.cos(rotationRad) * (y - shapeCenter.height / 2) +
			shapeCenter.height / 2 +
			position.y;

		vectors.push(new Vector(rotatedX, rotatedY));
	}

	return new Polygon(new Vector(0, 0), vectors); 
}


export function convertToSATPolygonForMultiplePanel(
	points: number[],
	position: { x: number; y: number }
): Polygon {
	const vectors: Vector[] = [];
	for (let i = 0; i < points.length; i += 2) {
		vectors.push(new Vector(points[i] + position.x, points[i + 1] + position.y));
	}
	return new Polygon(new Vector(0, 0), vectors);
}

export function getSnappedPositionForMultiplePanels(
	currentGroupPos: { x: number; y: number },
	currentPanelPoints: number[],
	siblings: Array<{ points: number[]; position: { x: number; y: number } }>,
	snapThreshold: number
): { x: number; y: number } | null {
	let closestSnapPoint: { x: number; y: number } | null = null;
	let minDistance = Infinity;

	const currentPolygon = convertToSATPolygonForMultiplePanel(currentPanelPoints, currentGroupPos);

	siblings.forEach((sibling) => {
		const siblingPolygon = convertToSATPolygonForMultiplePanel(sibling.points, sibling.position);

		const response = new Response();
		const collided = testPolygonPolygon(currentPolygon, siblingPolygon, response);

		if (collided) {
			const overlapVector = response.overlapV.clone().scale(-1);
			closestSnapPoint = {
				x: currentGroupPos.x + overlapVector.x,
				y: currentGroupPos.y + overlapVector.y
			};
			minDistance = 0; 
		} else {
			currentPolygon.points.forEach((cv) => {
				siblingPolygon.points.forEach((sv) => {
					const distance = Math.hypot(sv.x - cv.x, sv.y - cv.y);
					if (distance < snapThreshold && distance < minDistance) {
						minDistance = distance;
						closestSnapPoint = {
							x: currentGroupPos.x + (sv.x - cv.x),
							y: currentGroupPos.y + (sv.y - cv.y)
						};
					}
				});
			});
		}
	});

	return closestSnapPoint;
}
export function bakeGroupTransform(
	group: Konva.Group,
) {
	const transform = group.getTransform().copy();

	group.children.forEach((child) => {
		if (!(child instanceof Konva.Line)) return;

		const pts = child.points();
		const newPts: number[] = [];

		for (let i = 0; i < pts.length; i += 2) {
			const p = transform.point({ x: pts[i], y: pts[i + 1] });
			newPts.push(p.x, p.y);
		}

		child.points(newPts);
	});

	group.offset({ x: 0, y: 0 });
	group.position({ x: 0, y: 0 });
}

type bbox = {
	width: number;
	height: number;
	x: number;
	y: number;
}
function isBoundingBoxesIntersect(boxA: bbox, boxB: bbox) {
	return !(
		boxA.x > boxB.x + boxB.width ||
		boxA.x + boxA.width < boxB.x ||
		boxA.y > boxB.y + boxB.height ||
		boxA.y + boxA.height < boxB.y
	);
}

function getFlatPolygonBoundingBox(points: number[]): bbox {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (let i = 0; i < points.length; i += 2) {
		minX = Math.min(minX, points[i]);
		maxX = Math.max(maxX, points[i]);
		minY = Math.min(minY, points[i + 1]);
		maxY = Math.max(maxY, points[i + 1]);
	}
	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function normalizePointsToStage(shape: Konva.Line) {
	const stage = shape.getStage();
	if (!stage) return [];
	const points = shape.points();
	const stagePosition = stage.position();

	const transfom = shape.getAbsoluteTransform();
	const normalizedPoints = [];
	for (let i = 0; i < points.length; i += 2) {
		const point = { x: points[i], y: points[i + 1] };
		const transformedPoint = transfom.point(point);
		const normalizedX = transformedPoint.x - stagePosition.x;
		const normalizedY = transformedPoint.y - stagePosition.y;
		normalizedPoints.push([normalizedX, normalizedY]);
	}

	return normalizedPoints.flat();
}

type PanelConfig = {
	orientation: RasterRoofSegment['orientation'];
	width: number;
	height: number;
}

export function getPanelPointsFromConfig(panel: PanelConfig, scale: Vector2d) {
	const scaledWidth = (panel.width) * scale.x;
	const scaledHeight = (panel.height) * scale.y;

	const isPortrait = panel.orientation === 'POTRAITT';

	const halfWidth = (isPortrait ? scaledHeight : scaledWidth) / 2;
	const halfHeight = (isPortrait ? scaledWidth : scaledHeight) / 2;

	// Define the points relative to the center (x, y)
	const points = [
		-halfWidth, - halfHeight,
		halfWidth, - halfHeight,
		halfWidth, halfHeight,
		- halfWidth, halfHeight,
		- halfWidth, - halfHeight,
	];

	return points;

}

export function getRotatedLinePoints(
	mousePos: { x: number; y: number },
	width: number,
	height: number,
	rotationDegrees: number,
) {
	const rotationRadians = rotationDegrees * (Math.PI / 180);

	// Calculate half-width and half-height
	const halfWidth = (width) / 2;
	const halfHeight = (height) / 2;

	// Original corner points based on the shape's dimensions
	const originalPoints = [
		-halfWidth, -halfHeight, // Top-left
		halfWidth, -halfHeight, // Top-right
		halfWidth, halfHeight, // Bottom-right
		-halfWidth, halfHeight // Bottom-left
	];

	const rotatedPoints = [];

	for (let i = 0; i < originalPoints.length; i += 2) {
		const x = originalPoints[i];
		const y = originalPoints[i + 1];

		// Rotate the point
		const rotatedX =
			mousePos.x + (x * Math.cos(rotationRadians) - y * Math.sin(rotationRadians));
		const rotatedY =
			mousePos.y + (x * Math.sin(rotationRadians) + y * Math.cos(rotationRadians));

		rotatedPoints.push(rotatedX, rotatedY);
	}

	return rotatedPoints;
}

export function checkIntersection(shapes: Konva.Line[], targetShape: Konva.Line, shouldHighlight = true) {
	let bestShape: Konva.Line | null = null;
	let maxIntersectionArea = 0; // Track the maximum intersection area

	// Convert target shape's points to a polygon
	const targetPolygon = lineToPolygon(normalizePointsToStage(targetShape));
	const targetBox = targetShape.getClientRect(); // Get the bounding box of the target shape

	for (const shape of shapes) {
		if (shouldHighlight) {
			shape.fill(FACET_HIGHLIGHT_OFF_COLOR);
		}
		// Convert each shape's points to a polygon
		const shapeBox = shape.getClientRect();

		// Check for bounding box intersection first to optimize
		if (!isBoundingBoxesIntersect(targetBox, shapeBox)) {
			continue;
		}

		const shapePolygon = lineToPolygon(normalizePointsToStage(shape));

		// Calculate intersection using polygon-clipping
		const intersectionPolygon = intersection(targetPolygon as Geom, shapePolygon as Geom);

		if (intersectionPolygon && intersectionPolygon.length > 0) {
			const intersectionArea = calculatePolygonArea(intersectionPolygon[0][0]); // Area of the intersection

			// Check if the current intersection area is greater than the max found so far
			if (intersectionArea > maxIntersectionArea) {
				maxIntersectionArea = intersectionArea;
				bestShape = shape;
			}
		}
	}

	if (bestShape && shouldHighlight) {
		bestShape.fill(FACET_HIGHLIGHT_COLOR); // Highlight the most intersected shape
	}

	return bestShape;
}

export const lineToPolygon = (linePoints: number[]) => {
	const polygon = [];
	for (let i = 0; i < linePoints.length; i += 2) {
		polygon.push([linePoints[i], linePoints[i + 1]]); // Pairs [x, y]
	}
	// Ensure the polygon is closed by repeating the first point at the end
	if (polygon.length > 0 && (polygon[0][0] !== polygon[polygon.length - 1][0] || polygon[0][1] !== polygon[polygon.length - 1][1])) {
		polygon.push(polygon[0]); // Close the polygon
	}
	return [polygon]; // Returning a MultiPolygon (array of polygons)
};

const calculatePolygonArea = (polygon: number[][]): number => {
	let area = 0;
	const n = polygon.length;

	for (let i = 0; i < n - 1; i++) {
		area += polygon[i][0] * polygon[i + 1][1] - polygon[i + 1][0] * polygon[i][1];
	}
	area += polygon[n - 1][0] * polygon[0][1] - polygon[0][0] * polygon[n - 1][1];
	return Math.abs(area) / 2; // Return absolute value divided by 2
};

export function generatePolygon(linePoints: number[]) {
	const vectors = linePoints.reduce<Vector[]>((acc, value, index) => {
		if (index % 2 === 0) {
			acc.push(new Vector(value, linePoints[index + 1]));
		}
		return acc;
	}, []);
	return new Polygon(new Vector(0, 0), vectors);
}

export const doesPolygonOverLap = (polyA: Polygon, polyB: Polygon, margin = 1.4): boolean => {
	const response = new Response();
	const collided = testPolygonPolygon(polyA, polyB, response);

	if (!collided) return false;
	return response.overlap > margin;
};

export const isOverLap = (movablePanel: Konva.Line, relativePanels: Konva.Line[], threshold = 1) => {
	const shrinkPolygon = (polygon: Polygon, threshold: number) => {
		const points = polygon.points.map((point) => point.clone()); // Clone the original points
		const centroidX = points.reduce((sum, point) => sum + point.x, 0) / points.length; // Avg X
		const centroidY = points.reduce((sum, point) => sum + point.y, 0) / points.length; // Avg Y
		const centroid = new Vector(centroidX, centroidY);

		// Scale points relative to the calculated centroid
		const shrunkPoints = points.map((point) => {
			const direction = point.clone().sub(centroid); // Direction from centroid
			return centroid.clone().add(direction.scale(1 - threshold)); // Scale inward
		});

		// Return a new polygon with the shrunk points
		return new Polygon(polygon.pos.clone(), shrunkPoints);
	};

	const extractPoints = (polygon: Polygon) => {
		const points: number[] = [];
		polygon.points.forEach((point) => {
			points.push(point.x, point.y);
		});
		return points;
	};

	const movablePolygon = convertToSATPolygon(movablePanel, movablePanel.position());
	const shrunkMovablePolygon = shrinkPolygon(movablePolygon, threshold);

	const predefinedPolygons = relativePanels.map((panel: Konva.Line) =>
		convertToSATPolygon(panel, panel.position())
	);

	// Debug: Get points of the shrunk polygon to visualize
	const shrunkPolygonPoints = extractPoints(shrunkMovablePolygon);

	const hasOverlap = predefinedPolygons.some((polygon: Polygon) =>
		testPolygonPolygon(shrunkMovablePolygon, polygon)
	);

	return { hasOverlap, shrunkPolygonPoints };
};
function removeDuplicatePoint(arr: number[][]): number[][] {
	if (arr[0][0] === arr[arr.length - 1][0] && arr[0][1] === arr[arr.length - 1][1]) {
		arr.pop();
	}
	return arr;
}

function isPointInPolygon(smallPolygon: number[][], polygon: number[][]): boolean {
	if (smallPolygon.length !== polygon.length) return false;

	const a1 = removeDuplicatePoint(smallPolygon);
	const b1 = removeDuplicatePoint(polygon);

	const sortArray = (arr: number[][]): number[][] => arr.sort((a, b) => a[0] - b[0]);
	const strArr1 = JSON.stringify(sortArray(a1));
	const strArr2 = JSON.stringify(sortArray(b1));

	return strArr1 === strArr2;
}

// Grows points outward from their own centroid by percent (0.1 = 10% bigger).
// Area-weighted, not vertex-average, so this also works for L-shaped/concave facets.
function padPolygonOutward(points: number[], percent: number): number[] {
	if (!percent) return points;

	let area = 0, centroidX = 0, centroidY = 0;
	const pointCount = points.length / 2;
	for (let i = 0; i < pointCount; i++) {
		const x0 = points[i * 2], y0 = points[i * 2 + 1];
		const next = (i + 1) % pointCount;
		const x1 = points[next * 2], y1 = points[next * 2 + 1];
		const cross = x0 * y1 - x1 * y0;
		area += cross;
		centroidX += (x0 + x1) * cross;
		centroidY += (y0 + y1) * cross;
	}
	area /= 2;
	if (!area) return points; // degenerate (zero-area) polygon — nothing sensible to pad

	centroidX /= 6 * area;
	centroidY /= 6 * area;

	const scale = 1 + percent;
	const padded: number[] = [];
	for (let i = 0; i < points.length; i += 2) {
		padded.push(centroidX + (points[i] - centroidX) * scale, centroidY + (points[i + 1] - centroidY) * scale);
	}
	return padded;
}

// Defaults to 0 padding, so every existing caller is unaffected.
export function isCompletelyInsideFacet(
	smallPolygonPoints: number[], largePolygonPoints: number[], threshold = 0.98, largePolygonPaddingPercent = 0
): boolean {
	const smallPolygon = lineToPolygon(smallPolygonPoints);
	const targetPolygon = lineToPolygon(padPolygonOutward(largePolygonPoints, largePolygonPaddingPercent));

	const intersectionResult = intersection(smallPolygon as Geom, targetPolygon as Geom);

	if (!intersectionResult?.[0]?.[0]?.length) return false;

	const smallPolygonArea = calculatePolygonArea(smallPolygon[0]);
	const intersectionArea = calculatePolygonArea(intersectionResult[0][0]);
	return intersectionArea >= smallPolygonArea * threshold;
}

// 0% only when both ground-mount flags are set (true ground mount); "Flat" alone still gets 10%.
export function getFacetInvalidPaddingPercent(roofSegment?: Pick<RasterRoofSegment, 'isGroundMount' | 'noGapGroundMount'>): number {
	return roofSegment?.isGroundMount && roofSegment?.noGapGroundMount ? 0 : 0.1;
}

// Row spacing gap (px) for flat facets, 0 otherwise.
export function getRowSpacingGapPx(
	roofSegment: Pick<RasterRoofSegment, 'isGroundMount' | 'noGapGroundMount' | 'rowSpacing'>,
	pxScale: number,
	scaleY: number
): number {
	const isTrueGroundMountWithSpacing = roofSegment.isGroundMount === true && !roofSegment.noGapGroundMount
		&& !!roofSegment.rowSpacing;

	if (isTrueGroundMountWithSpacing) {
		return (inchesToDecimeters(metersToInches(roofSegment.rowSpacing as number)) / pxScale) * scaleY;
	}

	return 0;
}

// Panel is "outside" only when it has zero overlap with the padded facet (threshold 0).
export function isPanelCompletelyOutsideFacet(panelPolygonPoints: number[], facetPolygonPoints: number[], facetPaddingPercent: number): boolean {
	const paddedFacetPoints = padPolygonOutward(facetPolygonPoints, facetPaddingPercent);

	// Bbox reject first — cheaper than the exact polygon test, same result either way.
	if (!isBoundingBoxesIntersect(getFlatPolygonBoundingBox(panelPolygonPoints), getFlatPolygonBoundingBox(paddedFacetPoints)))
		return true;

	return !isCompletelyInsideFacet(panelPolygonPoints, facetPolygonPoints, 0, facetPaddingPercent);
}

// How many outside panels invalidate the drag. Swap the rule by uncommenting one line.
type OutsidePanelThreshold = (totalPanels: number) => number;

const minOutsidePanelsForInvalid: OutsidePanelThreshold = () => 1; // ACTIVE: 1 panel outside
// const minOutsidePanelsForInvalid: OutsidePanelThreshold = total => Math.ceil(total / 2); // half outside
// const minOutsidePanelsForInvalid: OutsidePanelThreshold = total => total; // whole array outside

// Counts outside panels against minOutsidePanelsForInvalid; exits early either way.
export function isFacetArrayDragInvalid(panelPolygonPointsList: number[][], facetPolygonPoints: number[], facetPaddingPercent: number): boolean {
	const totalPanels = panelPolygonPointsList.length;
	if (!totalPanels) return false;

	const requiredOutside = minOutsidePanelsForInvalid(totalPanels);
	let outsideCount = 0;

	for (let i = 0; i < totalPanels; i++) {
		if (isPanelCompletelyOutsideFacet(panelPolygonPointsList[i], facetPolygonPoints, facetPaddingPercent)) outsideCount++;
		if (outsideCount >= requiredOutside) return true;
		if (outsideCount + (totalPanels - 1 - i) < requiredOutside) return false;
	}

	return false;
}

export const rotateVector = ([x, y]: Point, angleRad: number): Point => {
	const cos = Math.cos(angleRad);
	const sin = Math.sin(angleRad);
	return [x * cos - y * sin, x * sin + y * cos];
};

export const generatePanelAt = (
	cx: number,
	cy: number,
	width: number,
	height: number,
	angleRad: number,
): RectPoints => {
	const halfW = width / 2;
	const halfH = height / 2;
	const corners: Point[] = [
		[-halfW, -halfH],
		[halfW, -halfH],
		[halfW, halfH],
		[-halfW, halfH],
	];
	const transformed = corners.map(([dx, dy]) => {
		const [x, y] = rotateVector([dx, dy], angleRad);
		return [cx + x, cy + y];
	});

	transformed.push(transformed[0]);

	return transformed.flat();
};

export function getClickedPanel(
	panels: Konva.Line[],
	pointer: { x: number; y: number }
): Konva.Line | null {
	// Tiny 1px square polygon at the pointer location
	const pointPoly = new Polygon(
		new Vector(pointer.x, pointer.y),
		[
			new Vector(0, 0),
			new Vector(1, 0),
			new Vector(1, 1),
			new Vector(0, 1),
		]
	);

	for (const panel of panels) {
		const pts = panel.points(); // [x1,y1,x2,y2,...]

		// Convert panel points → SAT polygon
		const vectors = [];
		for (let i = 0; i < pts.length; i += 2) {
			vectors.push(new Vector(pts[i], pts[i + 1]));
		}

		const panelPoly = new Polygon(new Vector(0, 0), vectors);

		// Apply transform: position + rotation
		panelPoly.pos.x = panel.x();
		panelPoly.pos.y = panel.y();
		panelPoly.setAngle((panel.rotation() * Math.PI) / 180);

		// SAT collision check
		const response = new Response();
		const collided = testPolygonPolygon(pointPoly, panelPoly, response);

		if (collided) {
			return panel;
		}
	}

	return null;
}

function getFacetAbsolutePolygon(facet: Konva.Line): number[][] {
	const abs = facet.getAbsoluteTransform();
	const pts = facet.points();
	const poly: number[][] = [];

	for (let i = 0; i < pts.length; i += 2) {
		const p = abs.point({ x: pts[i], y: pts[i + 1] });
		poly.push([p.x, p.y]);
	}

	// close polygon if needed
	if (poly.length && (
		poly[0][0] !== poly[poly.length - 1][0] ||
		poly[0][1] !== poly[poly.length - 1][1]
	)) {
		poly.push(poly[0]);
	}

	return poly;
}

function isPointInsidePolygon(point: { x: number; y: number }, polygon: number[][]) {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i][0], yi = polygon[i][1];
		const xj = polygon[j][0], yj = polygon[j][1];

		const intersect = ((yi > point.y) !== (yj > point.y)) &&
			(point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

		if (intersect) inside = !inside;
	}
	return inside;
}

export function getFacetUnderPointer(facets: Konva.Line[], pointer: { x: number; y: number }) {
	let found: Konva.Line | null = null;

	for (const facet of facets) {
		facet.fill(FACET_HIGHLIGHT_OFF_COLOR);
		const polygon = getFacetAbsolutePolygon(facet);

		if (isPointInsidePolygon(pointer, polygon)) {
			found = facet;
		}
	}
	if (found) {
		found.fill(FACET_HIGHLIGHT_COLOR);
	}

	return found;
}
export const findTargetFacetByCoverage = (
	allFacets: Konva.Line[],
	movableGroups: Konva.Group[],
	allRoofSegs: Array<RasterRoofSegment>,
	stage: Konva.Stage | null
): Konva.Line | null => {
	const maximumPanelsFacet: { [facetId: string]: number } = {};

	allFacets.forEach((eachFacet) => {
		const facetId = eachFacet.attrs.id || eachFacet.name();
		let validPanelCount = 0;
		const facetPts = normalizePointsToStage(eachFacet);
		const targetFacetDetails = allRoofSegs.find(eachSeg => eachSeg.id === facetId);
		if (!targetFacetDetails) return;
		movableGroups.forEach((group) => {
			group.children.forEach((eachChildPanel) => {
				if (!(eachChildPanel instanceof Konva.Line)) return;
				// const { x, y } = eachChildPanel.getAbsolutePosition();
				// let panelWidth = (targetFacetDetails.panelWidth);
				// let panelHeight = (targetFacetDetails.panelHeight);
				// let rotationAngle = (targetFacetDetails.azimuthDegrees) % 360;
				// rotationAngle = degToRad(rotationAngle);
				// const panelOrientation = eachChildPanel.attrs.orientation;
				// if (panelOrientation === ORIENTATION.LANDSCAPE && panelHeight > panelWidth) {
				// 	[panelWidth, panelHeight] = [panelHeight, panelWidth];
				// }
				// if (panelOrientation === ORIENTATION.PORTRAIT && panelWidth > panelHeight) {
				// 	[panelWidth, panelHeight] = [panelHeight, panelWidth];
				// }

				// const scaledPanelWidth = (panelWidth / pxScale) * scale.x;
				// const scaledPanelHeight = (panelHeight / pxScale) * Math.cos(targetFacetDetails.pitchDegrees * (Math.PI / 180)) * scale.y;

				// const globalPanelPts = generatePanelAt(x, y, scaledPanelWidth, scaledPanelHeight, rotationAngle);
				const panelPts = normalizePointsToStage(eachChildPanel);
				if (isCompletelyInsideFacet(panelPts, facetPts, 0.98)) {
					validPanelCount++;
				}
			});
		});

		if (validPanelCount > 0) {
			maximumPanelsFacet[facetId] = validPanelCount;
		}
	});

	const entries = Object.entries(maximumPanelsFacet);
	if (entries.length === 0) return null;

	const maxCount = Math.max(...entries.map(([, c]) => c));
	const candidates = entries.filter(([, c]) => c === maxCount);
	let maximumCoveredFacet = candidates[0][0];

	if (candidates.length > 1 && stage) {
		const pointer = stage.getPointerPosition();
		if (pointer) {
			let minDistance = Infinity;
			candidates.forEach(([id]) => {
				const facetNode = allFacets.find(f => (f.attrs.id || f.name()) === id);
				if (facetNode) {
					const box = facetNode.getClientRect();
					const center = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
					const dist = Math.hypot(pointer.x - center.x, pointer.y - center.y);
					if (dist < minDistance) {
						minDistance = dist;
						maximumCoveredFacet = id;
					}
				}
			});
		}
	}
	let targetFacet: Konva.Line | null = null;
	allFacets.forEach((f) => {
		const currentId = f.attrs.id || f.name();
		if (currentId === maximumCoveredFacet) {
			// f.fill(FACET_HIGHLIGHT_COLOR);
			targetFacet = f;
		} else {
			// f.fill(FACET_HIGHLIGHT_OFF_COLOR);
		}
	});

	return targetFacet;
};
export const getDynamicPanelPoints = (
	originalPoints: number[],
	pivot: { x: number; y: number },
	groupPos: { x: number; y: number },
	rotationRad: number,
	panelData: any,
	facetDetails: any,
	startPitch: number,
	pxScale: number,
	scale: { x: number; y: number },
	targetAzimuth: number
) => {
	const { x: dx, y: dy } = groupPos;
	const { x: px, y: py } = pivot;
	const cos = Math.cos(rotationRad);
	const sin = Math.sin(rotationRad);

	const xs = originalPoints.filter((_, i) => i % 2 === 0);
	const ys = originalPoints.filter((_, i) => i % 2 === 1);
	const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
	const cy = ys.reduce((a, b) => a + b, 0) / ys.length;

	const rx = cx - px;
	const ry = cy - py;

	const rotatedCenter = {
		x: px + (rx * cos - ry * sin) + dx,
		y: py + (rx * sin + ry * cos) + dy
	};

	let panelWidth = facetDetails.panelWidth;
	let panelHeight = facetDetails.panelHeight;

	if (panelData.orientation === 'LANDSCAPE' && panelHeight > panelWidth) {
		[panelWidth, panelHeight] = [panelHeight, panelWidth];
	}
	if (panelData.orientation === 'PORTRAIT' && panelWidth > panelHeight) {
		[panelWidth, panelHeight] = [panelHeight, panelWidth];
	}

	const dims = {
		width: (panelWidth / pxScale) * scale.x,
		height: (panelHeight / pxScale) * Math.cos((startPitch) * (Math.PI / 180)) * scale.y,
	};

	const absPoints = getRotatedLinePoints(rotatedCenter, dims.width, dims.height, targetAzimuth);

	return absPoints.map((p, i) => (i % 2 === 0 ? p - dx : p - dy));
};

export const rotatePoint = (x: number, y: number, angleDeg: number) => {
	const angleRad = (angleDeg * Math.PI) / 180;
	return {
		x: x * Math.cos(angleRad) - y * Math.sin(angleRad),
		y: x * Math.sin(angleRad) + y * Math.cos(angleRad),
	};
};
export const ensureClosedPolygon = (points: number[]): number[] => {
	if (points.length >= 2) {
		const [fx, fy] = [points[0], points[1]];
		const [lx, ly] = [points[points.length - 2], points[points.length - 1]];
		if (fx !== lx || fy !== ly) {
			return [...points, fx, fy];
		}
	}
	return points;
};